import React, { useEffect, useState } from 'react';
import { ChefHat, Sparkles } from 'lucide-react';

const parsingSteps = [
  { label: 'Reading your recipe...', icon: '📖' },
  { label: 'Identifying ingredients...', icon: '🥘' },
  { label: 'Organizing instructions...', icon: '📋' },
  { label: 'Extracting notes & tips...', icon: '💡' },
  { label: 'Polishing the details...', icon: '✨' },
];

const RecipeParsingProgress: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [dots, setDots] = useState('');

  // Animate through steps
  useEffect(() => {
    const stepInterval = setInterval(() => {
      setCurrentStep((prev) => {
        if (prev < parsingSteps.length - 1) {
          return prev + 1;
        }
        return prev;
      });
    }, 1500);

    return () => clearInterval(stepInterval);
  }, []);

  // Animate dots
  useEffect(() => {
    const dotInterval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? '' : prev + '.'));
    }, 400);

    return () => clearInterval(dotInterval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-violet-50 to-indigo-50 dark:from-gray-950 dark:via-purple-950/20 dark:to-gray-950 flex items-center justify-center">
      <div className="text-center px-6 max-w-md">
        {/* Animated Chef Hat */}
        <div className="relative inline-block mb-8">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center shadow-2xl animate-pulse">
            <ChefHat className="w-12 h-12 text-white" />
          </div>
          {/* Floating sparkles */}
          <Sparkles className="absolute -top-2 -right-2 w-6 h-6 text-amber-400 animate-bounce" style={{ animationDelay: '0.1s' }} />
          <Sparkles className="absolute -bottom-1 -left-3 w-5 h-5 text-purple-400 animate-bounce" style={{ animationDelay: '0.3s' }} />
          <Sparkles className="absolute top-1/2 -right-4 w-4 h-4 text-pink-400 animate-bounce" style={{ animationDelay: '0.5s' }} />
        </div>

        {/* Main message */}
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Cooking up your recipe{dots}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 mb-8">
          Our AI chef is reading and organizing everything
        </p>

        {/* Progress Steps */}
        <div className="space-y-3 text-left bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-2xl p-6 shadow-lg ring-1 ring-gray-200/50 dark:ring-gray-700/50">
          {parsingSteps.map((step, index) => (
            <div
              key={step.label}
              className={`flex items-center gap-3 transition-all duration-500 ${
                index < currentStep
                  ? 'opacity-50'
                  : index === currentStep
                  ? 'opacity-100 scale-105'
                  : 'opacity-30'
              }`}
            >
              <span className="text-xl w-8 text-center">{step.icon}</span>
              <span
                className={`text-sm ${
                  index === currentStep
                    ? 'font-semibold text-purple-700 dark:text-purple-300'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                {step.label}
              </span>
              {index < currentStep && (
                <span className="ml-auto text-green-500 text-sm">✓</span>
              )}
              {index === currentStep && (
                <span className="ml-auto">
                  <span className="inline-block w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Subtle hint */}
        <p className="mt-6 text-xs text-gray-400 dark:text-gray-500">
          This usually takes just a few seconds
        </p>
      </div>
    </div>
  );
};

export default RecipeParsingProgress;
